import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchEmployees } from '../../store/slices/authSlice';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import emailChanges from '../../data/emailChanges';
import { sendEmailMigrationNotification } from '../../emailjs/emailService';
import { convertObjectDates } from '../../utils/firebase-timestamp-transformation';
import CSVExport from './CSVExport';
import CSVUpload from './CSVUpload';
import './EmployeesList.scss';
import './CSVExport.scss';
import './CSVUpload.scss';

const EmployeesList = () => {
  const dispatch = useDispatch();
  const [migrating, setMigrating] = useState(null);
  const { 
    employees, 
    employeesLoading, 
    employeesError,
    userType
  } = useSelector(state => state.auth);

  useEffect(() => {
    if (employees.length === 0) {
      dispatch(fetchEmployees());
    }
  }, [dispatch, employees.length]);

  // Function to refresh employee list after upload
  const handleUploadComplete = () => {
    dispatch(fetchEmployees());
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };
  
  // Function to handle email migration
  const handleMigrateEmail = async (employee) => {
    const oldEmail = employee.email;
    const newEmail = findNewEmail(oldEmail);
    
    if (!newEmail) {
      alert(`No migration mapping found for ${oldEmail} in emailChanges.js`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to migrate ${oldEmail} to ${newEmail}?`)) {
      return;
    }
    
    try {
      setMigrating(employee.email);
      
      // Check if an employee with the new email already exists
      const myelinersRef = collection(db, 'myeliners');
      const newEmailQuery = query(myelinersRef, where('email', '==', newEmail));
      const newEmailSnapshot = await getDocs(newEmailQuery);
      
      if (!newEmailSnapshot.empty) {
        alert(`An employee with email ${newEmail} already exists in the database.`);
        setMigrating(null);
        return;
      }
      
      // Get full employee data and convert timestamp fields to JS Date objects
      const employeeData = { ...employee };
      const processedEmployeeData = convertObjectDates(employeeData);
      
      // Create new employee document with updated email
      const newEmployeeData = {
        ...processedEmployeeData,
        email: newEmail,
        oldEmail: oldEmail,
        emailMigrated: true,
        emailMigratedAt: new Date()
      };
      
      // Use the new email as the document ID
      const newEmployeeRef = doc(db, 'myeliners', newEmail);
      await setDoc(newEmployeeRef, newEmployeeData);
      
      // Send email notification to both old and new email addresses
      await sendEmailMigrationNotification(newEmployeeData, oldEmail, newEmail);
      
      alert(`Successfully migrated ${oldEmail} to ${newEmail}`);
      
      // Refresh employees list
      dispatch(fetchEmployees());
      
    } catch (error) {
      console.error('Error migrating employee:', error);
      alert(`Error migrating employee: ${error.message}`);
    } finally {
      setMigrating(null);
    }
  };

  if (userType !== 'admin') {
    return (
      <div className="employees-list-container">
        <div className="alert-message">
          You do not have permission to view employee information.
        </div>
      </div>
    );
  }

  if (employeesLoading) {
    return (
      <div className="employees-list-container">
        <div className="loading-indicator">Loading employees...</div>
      </div>
    );
  }

  if (employeesError) {
    return (
      <div className="employees-list-container">
        <div className="error-message">
          Error loading employees: {employeesError}
        </div>
      </div>
    );
  }

  return (
    <div className="employees-list-container">
      <h2 className="employees-list-title">Employees Directory</h2>
      
      <div className="csv-actions-container">
        <CSVUpload onUploadComplete={handleUploadComplete} />
        <CSVExport />
      </div>
      
      {employees.length === 0 ? (
        <div className="no-data-message">
          No employee records found.
        </div>
      ) : (
        <div className="employees-list">
          <div className="employees-list-header">
            <span className="employee-name">Name</span>
            <span className="employee-email">Email</span>
            <span className="employee-type">Type</span>
            <span className="employee-department">Department</span>
            <span className="employee-status">Status</span>
            <span className="employee-actions">Actions</span>
          </div>
          
          {employees.map((employee) => (
            <div key={employee.empID || employee.email} className="employee-item">
              <span className="employee-name">{employee.name || 'N/A'}</span>
              <span className="employee-email">
                {employee.email}
                {employee.emailMigrated && <div className="email-migrated-badge">Migrated</div>}
              </span>
              <span className="employee-type">{employee.userType || 'user'}</span>
              <span className="employee-department">{employee.department || 'N/A'}</span>
              <span className={`employee-status ${employee.status || 'active'}`}>
                {employee.status || 'Active'}
              </span>
              <span className="employee-actions">
                <Link 
                  to={`/myeliner/${encodeURIComponent(employee.email)}`} 
                  className="view-profile-btn"
                >
                  View Profile
                </Link>
                {findNewEmail(employee.email) && !employee.emailMigrated && (
                  <button 
                    className="migrate-email-btn"
                    onClick={() => handleMigrateEmail(employee)}
                    disabled={migrating === employee.email}
                  >
                    {migrating === employee.email ? 'Migrating...' : 'Migrate Email'}
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeesList; 